import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { consolidacaoDiariaService } from '../../services';
import { ConsolidacaoDiariaResumo } from '../../types/indicadores';

const formatDecimal = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatInteger = new Intl.NumberFormat('pt-BR');

const getInitialDate = () => new Date().toISOString().split('T')[0];

const parseErrorMessage = (error: unknown) => {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null) {
    const axiosError = error as Record<string, any>;
    if (axiosError.response?.data?.detail) return axiosError.response.data.detail;
    if (axiosError.response?.data?.message) return axiosError.response.data.message;
    if (axiosError.message) return axiosError.message;
  }
  return 'Não foi possível carregar a consolidação diária.';
};

export const ConsolidacaoDiariaPage: React.FC = () => {
  const [referenceDate, setReferenceDate] = useState(getInitialDate());
  const [data, setData] = useState<ConsolidacaoDiariaResumo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fetchData = useCallback(async () => {
    if (!referenceDate) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await consolidacaoDiariaService.fetch({ data: referenceDate });
      setData(response);
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [referenceDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const contagens = data?.contagens_diarias;
  const produtos = contagens?.por_produto ?? [];
  const errorMessage = parseErrorMessage(error);

  return (
    <Card className="shadow-sm border-0">
      <Card.Body>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
          <div>
            <h5 className="mb-1">Consolidação diária</h5>
            <p className="text-muted mb-0">
              Visualize o estoque consolidado e os totais das contagens registradas por produto.
            </p>
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <Form.Control
              type="date"
              value={referenceDate}
              onChange={(event) => setReferenceDate(event.target.value)}
              style={{ minWidth: 180 }}
            />
            <Button variant="primary" onClick={fetchData} disabled={loading || !referenceDate}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Atualizando
                </>
              ) : (
                'Atualizar'
              )}
            </Button>
          </div>
        </div>

        {errorMessage && (
          <Alert variant="danger" className="mt-4">
            {errorMessage}
          </Alert>
        )}

        {!loading && !error && !data && (
          <Alert variant="info" className="mt-4">
            Nenhuma consolidação encontrada para a data selecionada.
          </Alert>
        )}

        {data && (
          <>
            <Row className="mt-4 g-3">
              <Col md={6}>
                <Card className="h-100 border-0 bg-light">
                  <Card.Body>
                    <small className="text-uppercase text-muted">Estoque de madeira (ton)</small>
                    <h4 className="mb-0">{formatDecimal.format(data.estoque_madeira_ton)}</h4>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="h-100 border-0 bg-light">
                  <Card.Body>
                    <small className="text-uppercase text-muted">Estoque de produtos</small>
                    <h4 className="mb-0">{formatDecimal.format(data.estoque_produtos)}</h4>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mt-4">
              <Col>
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-4">
                      <div>
                        <small className="text-uppercase text-muted">Contagens registradas</small>
                        <h4 className="mb-0">{formatInteger.format(contagens?.total_registros ?? 0)}</h4>
                      </div>
                      <div className="text-md-end">
                        <small className="text-uppercase text-muted">Quantidade total</small>
                        <h4 className="mb-0">{formatDecimal.format(contagens?.quantidade_total ?? 0)}</h4>
                      </div>
                    </div>

                    <div className="table-responsive">
                      <Table hover size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Produto</th>
                            <th>Descrição</th>
                            <th className="text-end">Quantidade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {produtos.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-center text-muted py-3">
                                Nenhuma contagem consolidada para esta data.
                              </td>
                            </tr>
                          ) : (
                            produtos.map((produto, index) => (
                              <tr
                                key={`${produto.produto ?? produto.produto_codigo ?? 'produto'}-${index}`}
                              >
                                <td>{produto.produto_codigo ?? produto.produto ?? '-'}</td>
                                <td>{produto.produto_descricao ?? '-'}</td>
                                <td className="text-end">
                                  {formatDecimal.format(produto.quantidade_total)}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Card.Body>
    </Card>
  );
};
